import { LitElement, html, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { MessageProcessor, type A2uiClientAction, type ComponentApi, type SurfaceModel } from '@a2ui/web_core/v0_9';
import '@a2ui/lit/v0_9'; // registers <a2ui-surface>
import { api, HttpError, isRetryable } from '../api';
import { draftsView, makeClientId, needsHearing, sendDrafts } from '../drafts';
import { recordSnippet } from '../pronounce';
import { onSpeaking, speak, stopSpeaking } from '../tts';
import { playButton, playState, pronWait } from './wait-bits';
import { CATALOG_ID, SURFACE_ID, exerciseCatalog } from '../a2ui/catalog';
import { adoptAppStyles } from '../a2ui/adopt-styles';
import { go, setLeaveGuard, type Tend } from '../router';
import type { OwnTown } from '../town/own-town';
import { emptyTally, missedWords, summaryOf, tallyAnswer, type EndSummary, type Tally } from '../town/end-summary';
import { foldSegment, materialOf } from '../town/round-plan';
import type { RoundTown } from '../town/round-town';
import type { AttemptInput, ItemStatus, ReviewGate, ReviewQueueEntry, SkipReason } from '../types';
import { cardMs, cardShown } from '../card-time';
import { answerHidden, COMPONENT, givenText, listenAudioText, showAnswerOn, skipReasonsFor, withoutSaying } from './lesson-view';
import { errorText, t } from '../i18n';
import { hearableLine } from '../a2ui/components/ladder-cards';
import { backGlyph, ui } from '../bidi-html';
import { RoundMode } from './round-mode';
import { openAnswer, savedRecordingPath } from './capture-open';
import { answerLabel } from './newcomer-name';
import { armFeel, feel } from '../feel';
import './end-note';
import { revealBar } from './reveal-bar';
import { nearbyTends, nextTend } from '../town/next-tend';

const FSI = '⁨', PDI = '⁩';

/** An answer the server has not confirmed; its id makes a retry count once. Its card, for the round's bank. */
type UnsavedAttempt = { momentId: string; a: AttemptInput & { attemptId: string }; e?: ReviewQueueEntry; i?: number };
/** Saved; retryable; a generic refusal; or a removed-item refusal with its own Undo guidance. */
type SendResult = 'saved' | 'retry' | 'refused' | 'item-removed';

/**
 * "Card 2 of 7 · seen 3 times, right twice": the item's scored history in
 * plain words. The Leitner box stays internal (ticket v1-02).
 */
function cardLine(i: number, n: number, item: { attempts: number; passes: number }): string {
  if (!item.attempts) return t('review.cardOfNew', { i, n });
  const seen = item.attempts === 1 ? t('review.seenOnce')
    : item.attempts === 2 ? t('review.seenTwice')
      : t('review.seenMany', { n: item.attempts });
  const right = item.passes === 0 ? t('review.rightNone')
    : item.passes === 1 ? t('review.rightOnce')
      : item.passes === 2 ? t('review.rightTwice')
        : t('review.rightMany', { n: item.passes });
  return t('review.cardOf', { i, n, seen, right });
}

function isItemStatusConflict(error: unknown): boolean {
  return error instanceof HttpError && error.status === 409 && error.code === 'item-status-conflict';
}

/**
 * A moment's first lesson in the town (v1 ticket 41, land-view.ts): the day's cut of its pack as cards, and the
 * landing's town they run over.
 */
export interface TownLesson {
  momentId: string;
  /** The moment's title, for the screen's title. */
  title: string;
  entries: ReviewQueueEntry[];
  /** The landing's town, or null when none could start (the cards then cover an empty night). */
  town: RoundTown | null;
  /** A newcomer moment whose question waits for an answer (the revamp): the lesson ends on Answer {name}. */
  answer?: { momentId: string; name: string };
  /** The town on screen as the lesson starts: what fog, bricks and "what changed" are measured against. */
  before: OwnTown | null;
}

/** How long a walk waits for a late answer's town to be drawn before it lets the learner move on. */
const REFRESH_HOLD_MS = 4000;

/**
 * The daily round (v1 ticket 15, `GET /api/round`): one queue a day, in
 * slots. Easier cards left waiting, defence (ticket 20, empty for now), the
 * due words (at most 20, today's batch first), salvage (ticket 23, empty for
 * now), spelling cards, then the day's first looks. The town lies behind it
 * (round-mode.ts): a passed review card banks a brick, a first look hangs as
 * a sign under its lot, and "Build what I earned" plays the payoff. Stop here
 * ends the round where it is; the server resumes the rest.
 *
 * Same hard-gated mechanic as practice; every answer
 * posts to the item's home moment so stats stay single-shaped. A pass
 * climbs a box only on the word's own card or a harder one (the ladder).
 * A miss keeps the box and brings the word's easier card next (ticket 04):
 * passing it keeps the box, missing it steps the word back one; either way
 * it is due tomorrow. One left unanswered leads the next round.
 *
 * Beta (ticket 09): today's review batch comes first and its progress shows
 * at the top. Progress is always the server's count of saved answers, so an
 * answer that failed to save is shown as such and does not count.
 *
 * Tending the town (v1 ticket 27): with `tend` set, the same screen works a
 * district's run, one building's card or a shared word across a road instead
 * of the review queue, under the same rules, and ends over the town after it,
 * like the round.
 *
 * Every end (the round's, a tending round's, a first lesson's) is the end over
 * the town: the town after the sitting, a chip over each lot that changed
 * (round-town.ts `showChips`), and a sheet at the foot with one headline, the
 * score and the promise, any comeback, and the way on (`<end-note>`).
 *
 * A moment's first lesson (v1 ticket 41): with `lesson` set, the same screen runs the day's cut of the moment's
 * pack over the landing's town, under practice's test-mode rules (every card scored as in practice, its easier card
 * read, never said); a card on a word in the fog hangs as a sign under its lot (round-mode.ts). It ends like the
 * round, with no comeback, and offers the moment.
 *
 * A building tended from home's town (`signs`, with `tend` a word): the town is drawn behind the cards and each card
 * hangs as a sign under the building (round-mode.ts `useSigns`); its ways out fire `tend-close` for home to close it.
 * Its end is no sheet but a slim walk bar (`renderWalk`): once the end's lines and the houses still to tend
 * (`GET /api/town/tending`) are both in, the camera makes one move, out to the tended house and up to four nearby
 * ones, each glinting to be tapped next (`tend-next`); Next house only when none of them is on screen; Done leaves.
 */
@customElement('review-view')
export class ReviewView extends LitElement {
  /** A tended round from the town instead of the review queue. */
  @property({ attribute: false }) tend: Tend | null = null;
  /** A moment's first lesson in the town instead of the review queue (v1 ticket 41). */
  @property({ attribute: false }) lesson: TownLesson | null = null;
  /** Tending one building as a sign on it, over home's town (home-town.ts), instead of its own screen. */
  @property({ type: Boolean }) signs = false;
  /** Home's scene stays connected throughout a walk between tended houses. */
  @property({ attribute: false }) signTown: RoundTown | null = null;
  @property({ attribute: false }) tendedKeys: ReadonlySet<string> = new Set();
  @state() private nextState: 'idle' | 'loading' | 'ready' | 'error' = 'idle';
  /** The pick for Next house, wherever it is; null when no house needs tending. */
  @state() private nextKey: string | null = null;
  @state() private nextError = '';
  /** The nearby houses framed with the tended one, each glinting to be tapped next. */
  private nearby: { key: string; kind: 'due' | 'dark' }[] = [];
  /** How many glints the one framing left on screen; null until it has landed. */
  @state() private glintsOnScreen: number | null = null;
  /** The latest walk framing: an earlier flight's glint count never lands after a newer one started. */
  private placeGen = 0;
  /** The latest walk framing that has landed (its glints counted). */
  private landedGen = 0;
  /** The bar's top the walk's chips and glints were last placed for. */
  private walkTop = 0;
  /** Done took the focus when the walk bar came up (once per end). */
  private walkFocused = false;
  private nextGen = 0;
  /** The town before the round's first answer, to tell what the round changed. */
  private townBefore: OwnTown | null = null;
  /** The sitting's saved answers: the end's score, and the words to look at again. */
  private tally: Tally = emptyTally();
  /** The end's lines are on screen (or took too long): its chips can be framed over what is left of the screen. */
  private endReady = false;
  private endTimer: ReturnType<typeof setTimeout> | undefined;
  private sheetSeen: ResizeObserver | null = null;
  /** What the chips were last framed for ('' before the first time): the same view is never flown to twice. */
  private chipsPlaced = '';
  private chipFrame = 0;
  /** Saves on their way: the round's summary waits for them. */
  private inflight = new Set<Promise<unknown>>();
  /** Lets the learner's taps wake the effects' audio while the round is on screen (ticket 37). */
  private unarmFeel: (() => void) | null = null;
  @state() private queue: ReviewQueueEntry[] = [];
  @state() private dueCount = 0;
  @state() private i = 0;
  @state() private answered = false;
  /** Ticket 32: a miss left the answer off screen, so the bar offers Show me instead of Next. */
  @state() private showMe = false;
  /** The task whose answer is already recorded; queue splices must not transfer that state to another card. */
  @state() private recordedTaskId: string | null = null;
  @state() private done = false;
  @state() private loading = true;
  @state() private error = '';
  @state() private saving = false;
  @state() private pronPhase: 'idle' | 'recording' | 'scoring' = 'idle';
  @state() private pronTip = '';
  @state() private pronLevel = 0;
  @state() private pronError = '';
  private pronStop = { stop: false };
  @state() private skipMenu = false;
  /** Most recent removals stay undoable in reverse order for this open session. */
  @state() private removedKeys: Array<{
    momentId: string;
    itemKey: string;
    taskId: string;
    index: number;
    entries: Array<{ index: number; entry: ReviewQueueEntry }>;
    countedDue: boolean;
    previousStatus: ItemStatus;
    statusVersion: number;
    undoOperationId?: string;
    answerSequence: number;
    answered: boolean;
    showMe: boolean;
    recorded: boolean;
  }> = [];
  /** Reuse the same removal operation id if the server committed but its response was lost. */
  private pendingRemovalOperations = new Map<string, string>();
  @state() private actionError = '';
  @state() private speaking: string | null = null;
  /** Whether `speaking` is heard yet, or its voice still on its way (the play button prepares meanwhile). */
  @state() private heard = false;
  /** Beta: today's review requirement, re-read after every save. */
  @state() private gate?: ReviewGate;
  /** The requirement was met during this session. */
  @state() private unlockedNow = false;
  /** Answers whose save failed: kept for Retry, across cards and rounds, until they land. */
  @state() private unsaved: UnsavedAttempt[] = [];
  @state() private retrying = false;
  /** Incremented when an answer is submitted, closing Undo for removals before it. */
  private answerSequence = 0;
  /** Answers the server refused outright: Retry can't help, so they are only counted until dismissed. */
  @state() private lost = 0;
  @state() private attemptError = '';
  /** Leaving (in the app, Back, closing the tab) with unsaved answers asks first: they live only here. */
  private leaveGuard = () => (this.unsaved.length ? t('lesson.leaveUnsaved') : null);

  /**
   * Home, or, tending a building as a sign on home's town, back to that town (`tend-close`): home-town asks about
   * unsaved answers there, once, as go() does here.
   */
  private leave = () => {
    if (!this.signs) { go('/'); return; }
    this.dispatchEvent(new CustomEvent('tend-close', { bubbles: true, composed: true }));
  };
  @state() private micNote = '';
  /** The thumb zone watched for its height (see updateSignZone). */
  private zoneSeen: HTMLElement | null = null;
  private zoneWatch?: ResizeObserver;
  /** A check was refused this session (spent allowance, no scoring): later say-it cards read instead (ticket 14). */
  private checksOff = false;
  private gateGen = 0;
  private unsubSpeaking: (() => void) | null = null;
  private autoTimer: ReturnType<typeof setTimeout> | undefined;
  @state() private surface?: SurfaceModel<never>;

  /** The daily round's town, bricks and payoff (v1 ticket 15); unused while tending. */
  private round = new RoundMode(this);
  /**
   * Keys reached in an earlier segment of this sitting (v1 ticket 15 review): the
   * beta gate can split one round into several segments — `continueReview()`
   * replaces `this.queue` for the next one — but the bricks banked keep growing
   * across all of them, so "what changed" must too. Folded in as each segment
   * ends, and reset with the bricks when a payoff has already played.
   */
  private sittingReached = new Set<string>();

  private processor: MessageProcessor<ComponentApi> | undefined;
  private actionHandler = (a: A2uiClientAction) => this.onAction(a);

  protected createRenderRoot() { return this; }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.visualViewport?.removeEventListener('resize', this.syncKeyboard);
    window.visualViewport?.removeEventListener('scroll', this.syncKeyboard);
    setLeaveGuard(null);
    this.unarmFeel?.();
    this.unarmFeel = null;
    this.unsubSpeaking?.();
    this.unsubSpeaking = null;
    this.zoneWatch?.disconnect();
    this.zoneWatch = undefined;
    this.zoneSeen = null;
    this.clearAutoAdvance();
    this.clearEnd();
    this.processor?.model.dispose();
    this.processor = undefined;
  }

  /** On iOS the keyboard covers the fixed zone instead of resizing the page: lift the zone by what it covers. */
  private syncKeyboard = () => {
    const v = window.visualViewport;
    const covered = v ? Math.max(0, Math.round(window.innerHeight - v.height - v.offsetTop)) : 0;
    const kb = covered > 80 ? covered : 0;
    this.style.setProperty('--kb', `${kb}px`);
    this.style.setProperty('--sign-floor', kb ? '40px' : '96px');
  };

  async connectedCallback() {
    super.connectedCallback();
    window.visualViewport?.addEventListener('resize', this.syncKeyboard);
    window.visualViewport?.addEventListener('scroll', this.syncKeyboard);
    this.syncKeyboard();
    setLeaveGuard(this.leaveGuard);
    this.unarmFeel = armFeel();
    this.processor = new MessageProcessor([exerciseCatalog], this.actionHandler);
    this.unsubSpeaking = onSpeaking((now, heard) => {
      this.speaking = now;
      this.heard = heard;
    });
    this.processor.onSurfaceCreated((s) => {
      if (s.id === SURFACE_ID) this.surface = s as unknown as SurfaceModel<never>;
    });
    this.processor.onSurfaceDeleted((id) => {
      if (id === SURFACE_ID) this.surface = undefined;
    });
    await this.load();
  }

  private async load() {
    this.loading = true;
    // A tending or lesson round has no round of its own: the dawn reads only its passed cards.
    this.round.sitting = { done0: 0, total: 0, reached: () => this.reached(), passed: () => this.tally.right };
    try {
      if (this.lesson) {
        // The landing drew the town and holds the cards; the lesson runs over them.
        this.queue = this.lesson.entries;
        this.townBefore = this.lesson.before;
        await this.round.useTown(this.lesson.town, this.lesson.before);
      } else if (this.tend) {
        // Nothing is answered yet, so the town read alongside the round is the town before it.
        const [before, r] = await Promise.all([this.signTown?.shown ?? api.town().catch(() => null), tendQueue(this.tend)]);
        this.townBefore = before;
        this.queue = r.queue;
        if (this.signs && r.queue.length) {
          this.round.useSigns(this.signTown, before);
          await Promise.race([this.round.begin(before), new Promise((ok) => setTimeout(ok, 4000))]);
        }
      } else {
        // The day's round (v1 ticket 15), with the town before its first answer to draw behind it.
        // A reminder's tap (v1 ticket 38, `/round?reminder=<n>`) asks the server to lead with the word it asked.
        const reminder = new URLSearchParams(location.search).get('reminder') ?? undefined;
        const [r, before] = await Promise.all([api.round(reminder), api.town().catch(() => null)]);
        this.queue = r.queue;
        this.dueCount = r.dueCount;
        this.gate = r.gate;
        this.round.sitting = { done0: r.done, total: r.done + r.queue.length, reached: () => this.reached(), passed: () => this.tally.right };
        if (r.queue.length) {
          const ready = this.round.begin(before);
          // A round that opens on a first look hangs it in the town, so the town gets a moment to start.
          if (r.queue[0].firstLook) await Promise.race([ready, new Promise((ok) => setTimeout(ok, 4000))]);
        }
      }
      if (this.queue.length) this.renderTask();
    } catch (e) {
      this.error = errorText(e);
    } finally {
      this.loading = false;
    }
  }

  /** Another round after the queue ran out with today's batch still open (a skip or a failed save). */
  private continueReview = async () => {
    // Fold this segment's reached cards in before `load()` replaces the queue for the next one.
    if (this.round.phase === 'built') this.tally = emptyTally();
    this.sittingReached = foldSegment(this.sittingReached, this.round.phase === 'built', this.reachedKeys());
    this.clearEnd();
    this.clearAutoAdvance();
    // The next segment serves a new queue: the old segment's removals name
    // cards and indices that no longer exist, so Undo must not survive the
    // swap (its guard only watches the answer sequence).
    this.removedKeys = [];
    this.i = 0;
    this.answered = false;
    this.showMe = false;
    this.recordedTaskId = null;
    this.done = false;
    this.micNote = '';
    this.skipMenu = false;
    this.actionError = '';
    this.resetPron();
    this.round.again();
    await this.load();
  };

  /** Post one answer. A failed save is shown and counts toward today's review only once Retry lands it. */
  private async save(e: ReviewQueueEntry, a: AttemptInput): Promise<void> {
    // No step is sent: the server saves the answer at the step it served the card at.
    this.answerSequence += 1;
    this.saving = true;
    try {
      await this.saveOrKeep({ momentId: e.momentId, a: {
        ...a, ...(e.task.exposureId ? { exposureId: e.task.exposureId } : {}),
        ...(!a.revealed && !a.skipped && !a.selfReport ? { interaction: 'first_attempt' as const } : {}),
        attemptId: makeClientId(), ...cardMs(e.momentId, a.taskId),
      }, e, i: this.i });
    } finally {
      this.saving = false;
    }
  }

  /** A failed save is kept for Retry rather than lost silently; a refused one is said, not retried. */
  private async saveOrKeep(u: UnsavedAttempt) {
    // A save during a Retry joins it, so the answers still land oldest first.
    if (this.retrying) {
      this.unsaved = [...this.unsaved, u];
      return;
    }
    const r = await this.send(u);
    if (r === 'retry') this.unsaved = [...this.unsaved, u];
    else if (r === 'refused') this.lost += 1;
  }

  /**
   * Send one answer to the learner's record. The attempt response already
   * carries today's gate for a beta learner, so that is applied directly; a
   * send with no gate in its response, one that failed, or one whose gate is
   * stale by the time it lands (a later save started first) falls back to a
   * full re-read. A Retry goes through here too, so a late answer counts.
   */
  private async send(u: UnsavedAttempt): Promise<SendResult> {
    const gen = ++this.gateGen;
    let review: ReviewGate | undefined;
    try {
      const p = api.recordAttempt(u.momentId, u.a);
      this.inflight.add(p);
      const r = await p.finally(() => this.inflight.delete(p));
      if (u.a.exposureId) void api.exerciseEvent(u.momentId, u.a.exposureId, makeClientId(), 'browser_ack').catch(() => undefined);
      review = r.review;
      if (r.leftFog) feel('fog');
      // The first lesson follows practice: its easier card is always read, never said.
      if (r.retreat) this.queueRetreat(this.lesson ? withoutSaying(r.retreat) : r.retreat);
      if (u.e && (!this.tend || this.signs)) this.round.saved(u.i ?? -1, u.e, u.a, r);
      if (u.e) {
        this.tally = tallyAnswer(this.tally, { ...u.a, spelling: u.e.spelling, graded: r.graded }, u.e.item.key, u.e.item.latin || u.e.item.ca, r);
      }
      return 'saved';
    } catch (err) {
      console.warn('could not record attempt', err);
      if (err instanceof HttpError && err.code === 'attempt-item-removed') {
        this.attemptError = errorText(err);
        return 'item-removed';
      }
      return isRetryable(err) ? 'retry' : 'refused';
    } finally {
      // review may be stale by the time it lands, if a later save started
      // first: applyGate then leaves it unapplied, and this falls back to
      // a fresh read instead of silently dropping the answer's progress.
      if (!review || !this.applyGate(gen, review)) void this.refreshGate();
    }
  }

  /**
   * The easier card after a miss (ticket 04), asked next. The server keeps it
   * waiting until it is answered, so once the round is over it leads the next
   * one instead.
   */
  private queueRetreat(e: ReviewQueueEntry) {
    if (this.done || !this.queue.length) return;
    if (this.queue.some((x, k) => k > this.i && x.retreat && x.item.key === e.item.key)) return;
    const at = this.i + 1;
    this.queue = [...this.queue.slice(0, at), { ...e, retreat: true }, ...this.queue.slice(at)];
  }

  /** Re-send the unsaved answers oldest first, same ids; stop at the first that still fails. */
  private retryUnsaved = async () => {
    if (this.retrying) return;
    this.retrying = true;
    while (this.unsaved.length) {
      const u = this.unsaved[0];
      const r = await this.send(u);
      if (r === 'retry') break;
      this.unsaved = this.unsaved.filter((x) => x !== u);
      if (r === 'refused') this.lost += 1;
    }
    // An answer that landed late may have changed the town: the end's counts
    // and chips were read (or frozen by the payoff) before it arrived. A walk
    // stays held (`retrying`) until the town it continues on is drawn again,
    // but no longer than REFRESH_HOLD_MS: the picture is extra, and the next
    // house's end and Done read the town again.
    if (this.done && this.round.phase === 'built') {
      const refreshed = this.round.refreshChanges().then(() => this.placeChips());
      await Promise.race([refreshed, new Promise((r) => setTimeout(r, REFRESH_HOLD_MS))]);
    }
    this.retrying = false;
  };

  /** Non-blocking: the review goes on; Retry sends the same answers again, a refusal is only dismissed. */
  private renderUnsaved() {
    const n = this.unsaved.length;
    const lost = this.lost;
    const attemptError = this.attemptError;
    if (!n && !lost && !attemptError) return nothing;
    return html`
      ${n
        ? html`<div class="note save-error" role="alert" style="margin-top:6px;display:flex;gap:8px;align-items:center">
            <span style="flex:1">${n === 1 ? t('lesson.saveFailed') : t('lesson.saveFailedMany', { n })}</span>
            <button class="btn small" @click=${this.retryUnsaved} ?disabled=${this.retrying || this.saving}>
              ${this.retrying ? t('lesson.saving') : t('common.retry')}
            </button>
          </div>`
        : nothing}
      ${lost
        ? html`<div class="note save-error" role="alert" style="margin-top:6px;display:flex;gap:8px;align-items:center">
            <span style="flex:1">${lost === 1 ? t('lesson.saveRefused') : t('lesson.saveRefusedMany', { n: lost })}</span>
            <button class="btn small" @click=${() => (this.lost = 0)}>${t('common.dismiss')}</button>
          </div>`
        : nothing}
      ${attemptError
        ? html`<div class="note save-error" role="alert" style="margin-top:6px;display:flex;gap:8px;align-items:center">
            <span style="flex:1">${attemptError}</span>
            <button class="btn small" @click=${() => (this.attemptError = '')}>${t('common.dismiss')}</button>
          </div>`
        : nothing}
    `;
  }

  /** Re-read today's progress from the server; only the newest read may write. */
  private async refreshGate() {
    if (this.gate?.status !== 'pending') return;
    const gen = ++this.gateGen;
    try {
      const a = await api.allowance();
      this.applyGate(gen, a.limit !== null ? a.review : undefined);
    } catch { /* progress stays as shown; the server decides either way */ }
  }

  /**
   * Apply a freshly read gate: unlock plus a drafts flush on satisfied or
   * waived. Ignored once nothing is pending any more (already applied once,
   * or nothing to gate in the first place) or once a newer read has landed;
   * returns whether it applied, so a stale caller can fall back.
   */
  private applyGate(gen: number, g: ReviewGate | undefined): boolean {
    if (this.gate?.status !== 'pending' || !g || gen !== this.gateGen) return false;
    if (g.status === 'satisfied' || g.status === 'waived') {
      this.unlockedNow = true;
      // Drafts held back for today's review can go now.
      void sendDrafts();
    }
    this.gate = g;
    return true;
  }

  private get hasUnheardDraft() {
    return draftsView().drafts.some(needsHearing);
  }

  private openCaptureAfterReview = () => {
    const draft = draftsView().drafts.find(needsHearing);
    go(draft ? savedRecordingPath(draft.id) : '/record');
  };

  private renderGateStatus() {
    if (this.gate?.status === 'pending') {
      return html`<div class="card review-gate-progress" role="status" style="margin-bottom:8px">
        <strong>${t('gate.progress', { done: this.gate.done, n: this.gate.required })}</strong>
      </div>`;
    }
    if (this.unlockedNow) {
      return html`<div class="card review-gate-unlocked" role="status" style="margin-bottom:8px">
        <strong>${t('gate.unlocked')}</strong>
        <button class="btn primary block review-gate-record" style="margin-top:8px" @click=${this.openCaptureAfterReview}>${t(this.hasUnheardDraft ? 'drafts.resumeRecording' : 'gate.newMoment')}</button>
      </div>`;
    }
    return nothing;
  }

  protected updated(changed: PropertyValues) {
    if (this.walking() && this.done && this.round.phase === 'built') {
      if (this.nextState === 'idle') void this.findNext();
      const el = this.round.townEl;
      if (el) el.glintsOff = !!this.unsaved.length || this.retrying;
      const done = this.querySelector<HTMLButtonElement>('.tend-walk .tend-back');
      if (done && !this.walkFocused) {
        this.walkFocused = true;
        done.focus({ preventScroll: true });
      }
    }
    if (changed.has('answered') && this.answered) revealBar(this);
    adoptAppStyles(this.querySelector('a2ui-surface')?.shadowRoot);
    this.updateSignZone();
    this.watchSheet();
    // The pronunciation check is in flight: a say-it card hides "I don't know" until it has judged.
    this.querySelector('a2ui-surface')?.shadowRoot?.querySelector('li-sayfrommeaning')
      ?.toggleAttribute('data-checking', this.pronPhase === 'scoring');
    const e = this.queue[this.i];
    if (e && !this.loading && !this.done) cardShown(e.momentId, e.task.id);
  }

  private exerciseEvent(e: ReviewQueueEntry, kind: 'hint' | 'retry' | 'reveal' | 'browser_ack') {
    if (e.task.exposureId) void api.exerciseEvent(e.momentId, e.task.exposureId, makeClientId(), kind).catch(() => undefined);
  }

  render() {
    const round = !this.tend;
    const title = this.tend ? this.tendTitle(this.tend) : this.lesson ? this.lessonTitle(this.lesson) : t('round.title');
    return html`
      ${this.round.renderTown()}
      <div class="screen round-screen">
        <div class="topbar">
          <button class="btn ghost icon" @click=${this.leave} aria-label=${t('common.homeAria')}>${backGlyph()}</button>
          <h1 class="round-title">${title}</h1>
          ${round && !this.loading && !this.error && !this.done && this.queue.length
            ? html`<button class="btn small round-stop" @click=${this.stopHere}>${t('round.stop')}</button>`
            : nothing}
        </div>
        ${this.loading || this.hanging() || this.round.phase !== 'cards' ? nothing : this.renderGateStatus()}

        ${this.loading
          ? html`<div class="empty"><span class="spinner"></span></div>`
          : this.error
            ? html`<div class="card"><strong>${t('review.loadError')}</strong><div class="muted small">${this.error}</div></div>`
            : !this.queue.length && this.tend
              ? html`<div class="empty tend-empty">
                  <div class="big">✓</div>
                  <div>${t('tend.nothing')}</div>
                  <div class="small">${t(this.tend.kind === 'district' ? 'tend.nothingDistrictHint' : 'tend.nothingHint')}</div>
                  <button class="btn" style="margin-top:12px" @click=${this.leave}>${t('tend.backToTown')}</button>
                </div>`
            : !this.queue.length && this.lesson
              ? html`<div class="empty land-lesson-empty">
                  <div class="big">✓</div>
                  <div>${t('land.lessonDone')}</div>
                  <div class="end-actions">${this.renderLessonEnd(true)}</div>
                </div>`
            : !this.queue.length
              ? html`<div class="empty">
                  <div class="big">✓</div>
                  <div>${t('round.nothing')}</div>
                  <div class="small">${t('review.nothingDueHint')}</div>
                  <button class="btn" style="margin-top:12px" @click=${() => go('/')}>${t('common.home')}</button>
                </div>`
              : this.done
                ? this.renderEnd()
                : this.hanging()
                  ? nothing
                  : html`${round ? this.round.renderBank() : nothing}${this.renderCard()}`}
        ${this.removedKeys.length ? html`<button class="btn" style="margin-top:8px" @click=${this.undoRemoval} ?disabled=${this.saving || this.removedKeys.at(-1)?.answerSequence !== this.answerSequence}>${t('lesson.undoRemoval')}</button>` : nothing}
        ${this.done && this.actionError ? html`<div class="error small" role="alert" style="margin-top:8px">${this.actionError}</div>` : nothing}
      </div>
      ${this.hanging() && !this.loading && !this.done ? this.renderSign() : nothing}
    `;
  }

  /**
   * A card hanging as a sign under its lot (the dream sign): the card's stem in a cream bubble there, its question
   * and buttons in the thumb zone fixed at the screen's foot. Every card but a flashcard lays its own zone (it holds
   * the beat); a flashcard sits whole in the bubble and its zone is drawn here. The sign's height stops above the
   * zone, whose height `updated()` measures into --sign-zone-h.
   */
  private renderSign() {
    const spot = this.round.sign!.spot;
    const splits = this.queue[this.i].task.type !== 'flashcard';
    // On a short screen the bubble slides up over its building rather than under the zone: it keeps a floor of 96px.
    // With the keyboard up (--kb) the floor gives way to 40px; the top never leaves the viewport.
    const topAt = (vh: string) => `max(8px, min(${spot.top}px, calc(${vh} - var(--safe-bottom) - 16px - var(--sign-zone-h, 0px) - var(--kb, 0px) - 12px - var(--sign-floor, 96px))))`;
    const room = (vh: string) => `calc(${vh} - ${topAt(vh)} - var(--safe-bottom) - 16px - var(--sign-zone-h, 0px) - var(--kb, 0px) - 12px)`;
    // The tail is kept off the bubble's rounded corners.
    const px = Math.max(34, Math.min(spot.width - 34, spot.px));
    const { top, foot } = splits ? { top: nothing, foot: nothing } : this.signParts();
    return html`<div class="round-sign bubble" style="left:${spot.left}px;top:${topAt('100vh')};top:${topAt('100svh')};width:${spot.width}px;--px:${px}px;max-height:${room('100vh')};max-height:${room('100svh')}">
        ${this.renderCard()}
      </div>
      <!-- The bubble's tail sits outside its scroller, which would clip it. -->
      <i class="sign-tail" style="left:${spot.left + px - 12}px;top:calc(${topAt('100vh')} - 12px);top:calc(${topAt('100svh')} - 12px)"></i>
      ${splits ? nothing : html`<div class="sign-zone" dir=${ui()}>${top}${foot}</div>`}`;
  }

  /** The round's first look hangs as a sign under its lot in the town (v1 ticket 15) instead of covering it. */
  private hanging(): boolean {
    return (!this.tend || this.signs) && this.round.sign?.i === this.i;
  }

  /** The current card: its line, its name, the card itself and its buttons. */
  private renderCard() {
    const audio = this.audioText();
    const hanging = this.hanging();
    return html`
      ${hanging
        ? this.renderSignHead()
        : html`<div class="card-head">
            ${this.queue[this.i].retreat
              ? html`<p class="card-name">${t('retreat.card')}</p>`
              : this.queue[this.i].card
                ? html`<p class="card-name">${t(`review.card.${this.queue[this.i].card}`)}</p>`
                : nothing}
            <span class="muted small">
              ${this.lesson ? t('land.cardOf', { i: this.i + 1, n: this.queue.length }) : cardLine(this.i + 1, this.queue.length, this.queue[this.i].item)}
            </span>
          </div>
          ${this.queue[this.i].retreat ? html`<p class="muted small retreat-hint" style="margin:0 0 8px">${t('retreat.hint')}</p>` : nothing}`}
      ${audio && !hanging
        ? html`<div class="card" style="display:flex;gap:10px;align-items:center;margin-bottom:8px">
            ${playButton(playState(this.speaking, this.heard, audio), this.playAudio)}
            <span class="muted small">${t('review.listenHint')}</span>
          </div>`
        : nothing}
      <div class="surface">
        ${this.surface
          ? html`<a2ui-surface ?sign=${hanging} giveup .surface=${this.surface}></a2ui-surface>`
          : html`<div class="surface-empty"><span class="muted">${t('lesson.loadingCard')}</span></div>`}
      </div>
      ${hanging
        ? nothing
        : html`<div class="actionbar column ${this.answered || this.showMe || speakingCard(this.queue[this.i]?.task.type) ? '' : 'waiting'}">
            ${this.renderPron()}
            <div style="display:flex;gap:8px">
              ${this.showMe
                ? html`<button class="btn primary block" style="flex:1" @click=${this.showAnswer} ?disabled=${this.saving}>${t('ex.showMe')}</button>`
                : html`<button class="btn" style="flex:0 0 auto" @click=${this.toggleSkipMenu} ?disabled=${this.saving}>${t('lesson.skip')}</button>
                  <button class="btn primary block" style="flex:1" @click=${this.next} ?disabled=${!this.answered || this.saving}>
                    ${this.i + 1 >= this.queue.length ? t('lesson.finish') : t('lesson.next')}
                  </button>`}
            </div>
            <button class="btn danger" style="width:100%" @click=${this.removeCurrentCard} ?disabled=${this.saving}>${t('lesson.removeAction')}</button>
            ${this.renderBarNotes()}
          </div>`}
    `;
  }

  /** A speaking card's pronunciation check: its button, the level while it listens, the wait, its tip or error. */
  private renderPron() {
    if (!speakingCard(this.queue[this.i]?.task.type)) return nothing;
    return html`<div>
      ${this.checkOpen()
        ? html`<button class="btn block pron-check ${this.pronPhase === 'scoring' ? 'preparing' : ''}" @click=${this.togglePron}
            ?disabled=${this.pronPhase === 'scoring' || this.saving}>
            ${this.pronPhase === 'recording' ? t('lesson.stopCheck') : this.pronPhase === 'scoring' ? t('lesson.scoring') : t('lesson.sayCheck')}
          </button>`
        : nothing}
      ${this.pronPhase === 'recording'
        ? html`<div class="level" style="margin-top:6px"><div style="width:${Math.round(this.pronLevel * 100)}%"></div></div>`
        : nothing}
      ${this.pronPhase === 'scoring'
        // Say it from the meaning hides the word until it is judged: its wait never shows it.
        ? pronWait(this.queue[this.i]?.task.type === 'sayitback' ? String(this.queue[this.i]?.task.props.ca ?? '') : '')
        : nothing}
      ${this.pronTip ? html`<div class="note" dir="auto" style="margin-top:6px">${this.pronTip}</div>` : nothing}
      ${this.pronError ? html`<div class="muted small" style="margin-top:6px">${t('lesson.pronErrorTail', { err: this.pronError })}</div>` : nothing}
    </div>`;
  }

  /** Under the buttons: a failed action, the answers not saved yet, the microphone's note and the skip menu. */
  private renderBarNotes() {
    return html`
      ${this.actionError
        ? html`<div class="muted small" role="alert" style="margin-top:6px">${this.actionError}</div>`
        : nothing}
      ${this.renderUnsaved()}
      ${this.micNote
        ? html`<div class="muted small mic-note" style="margin-top:6px">${this.micNote}</div>`
        : nothing}
      ${this.skipMenu && !this.showMe
        ? html`<div class="card skip-menu" style="margin-top:8px;display:flex;gap:8px;flex-wrap:wrap">
            <span class="muted small" style="width:100%">${t('lesson.whySkip')}</span>
            ${skipReasonsFor(this.queue[this.i]?.task.type ?? '').map((r) => html`<button class="btn" @click=${() => this.skipWithReason(r)}>${t(`skip.${r}`)}</button>`)}
          </div>`
        : nothing}`;
  }

  /**
   * The head of a card hanging as a sign: one status tag (the card and its word's material, or the fog) and, on a
   * spot it card, a round play button for its line. An easier card keeps its hint.
   */
  private renderSignHead() {
    const e = this.queue[this.i], r = this.round;
    const lifted = r.lifted === this.i, climbed = r.latest?.key === e.item.key;
    const m = climbed ? r.latest!.material : lifted ? 'scaffolding' : materialOf(e.item.box);
    const name = lifted && !climbed ? t('sign.outOfFog')
      : e.retreat ? t('retreat.card')
        : m === 'fog' ? t('review.card.firstLook')
          : e.card ? t(`review.card.${e.card}`) : '';
    const state = m === 'fog' ? t('sign.inFog') : t(`round.material.${m}`);
    // The play button: a spot it card's line, or what a by-ear card plays (heard only, so its button is the big one).
    const line = e.task.type === 'spotit' ? ((e.task.props.words as string[] | undefined) ?? []).join(' ') : this.audioText() ?? '';
    const play = line && hearableLine(line) ? playState(this.speaking, this.heard, line) : null;
    const bigPlay = e.task.type === 'soundalike' || e.task.type === 'dictation' || e.task.type === 'listen';
    return html`
      <div class="sign-row">
        <span class="sr-only">${t('land.cardOf', { i: this.i + 1, n: this.queue.length })}</span>
        <span class="sign-tag ${lifted || climbed ? 'lit' : ''}" role="status" dir=${ui()}><i></i><span>${name
          ? html`<span class=${lifted && !climbed ? '' : 'card-name'}>${name}</span>${t('common.dot')}`
          : nothing}${state}</span></span>
        ${play
          ? html`<button class="sign-play ${play} ${bigPlay ? 'big' : ''}" @click=${this.playLine} aria-busy=${play === 'preparing' ? 'true' : 'false'}
              aria-label=${play === 'rest' ? t('sign.play') : t('sign.stop')}>${play === 'rest' ? '▶' : '■'}</button>`
          : nothing}
      </div>
      ${e.retreat ? html`<p class="muted small retreat-hint" style="margin:0 0 8px">${t('retreat.hint')}</p>` : nothing}`;
  }

  /**
   * The thumb zone of a hanging card (a SignHost's parts): its top row (progress and Skip) and its foot (Next once
   * the card is answered, or Show me, then the notes). A spot it card lays its beat between them.
   */
  /** A card heard only (the by-ear choice): its zone says to play it first. */
  heardOnly() { return this.queue[this.i]?.task.type === 'listen'; }

  signParts() {
    const n = this.queue.length;
    // The eye gets dots or "3 / 12"; a screen reader hears the sign head's "Card 1 of 3".
    const progress = n > 8
      ? html`<span class="sign-count" aria-hidden="true">${t('sign.of', { i: this.i + 1, n })}</span>`
      : n > 1
        ? html`<span class="sign-dots" aria-hidden="true">${
            Array.from({ length: n }, (_, k) => html`<b class=${k <= this.i ? 'on' : ''}></b>`)}</span>`
        : html`<span></span>`;
    const top = html`<div class="sign-top" data-card=${this.i + 1}>
      ${progress}
      ${this.showMe ? nothing : html`<button class="sign-skip" @click=${this.toggleSkipMenu} ?disabled=${this.saving}>${t('lesson.skip')}</button>`}
      <button class="btn danger" @click=${this.removeCurrentCard} ?disabled=${this.saving}>${t('lesson.removeAction')}</button>
    </div>`;
    const foot = html`<div class="actionbar column sign-foot">
      ${this.renderPron()}
      ${this.showMe
        ? html`<button class="btn primary block show-me" @click=${this.showAnswer} ?disabled=${this.saving}>${t('ex.showMe')}</button>`
        : this.answered
          ? html`<button class="btn primary block" @click=${this.next} ?disabled=${this.saving}>
              ${this.i + 1 >= this.queue.length ? t('lesson.finish') : t('lesson.next')}
            </button>`
          : nothing}
      ${this.renderBarNotes()}
    </div>`;
    return { top, foot };
  }

  /**
   * The end of the cards. A round with bricks shows them and "Build what I earned" first; the payoff plays in the
   * town. Then (at once for a round with no bricks, a tending round or a lesson with none) the end over the town:
   * the town after the sitting with a chip over each lot that changed, and the end's sheet at the foot.
   */
  private renderEnd() {
    const r = this.round;
    if (r.phase === 'cards') return this.renderBeforeBuild();
    if (r.phase === 'building') {
      return html`<div class="round-after"><div class="card muted small round-building" role="status" style="text-align:center">
        ${r.bricks.length ? t('round.building') : html`<span class="spinner"></span>`}
      </div></div>`;
    }
    return this.walking() ? this.renderWalk() : this.renderSheet();
  }

  /** A house tended from home's town, one of a walk between houses: its end is the walk bar, not a sheet. */
  private walking(): boolean {
    return this.signs && this.tend?.kind === 'word';
  }

  /**
   * The walk bar at the foot: the end's headline and missed words (`<end-note compact>`), any unsaved answers, one
   * status line, then Done and, only when no nearby house glints on screen, Next house.
   */
  private renderWalk() {
    const held = !!this.unsaved.length || this.retrying;
    const n = this.glintsOnScreen ?? 0;
    const status = this.nextState === 'error'
      ? html`<div class="small tend-walk-status" role="alert"><span>${this.nextError}</span>
          <button class="btn small tend-retry" @click=${() => void this.findNext()}>${t('tend.retryNext')}</button></div>`
      : this.nextState === 'ready' && !this.nextKey
        ? html`<div class="small tend-walk-status tend-walk-done" role="status">${t('tend.allTended')}</div>`
        : this.nextState === 'ready' && n
          ? html`<div class="small tend-walk-status tend-walk-nearby" role="status">${n === 1 ? t('tend.nearbyOne') : t('tend.nearby', { n })}</div>`
          : html`<div class="small tend-walk-status" role="status"></div>`;
    return html`<div class="tend-walk" @end-ready=${this.onEndReady}>
      <end-note compact .landed=${this.landed()} .summary=${this.summary()} .missed=${missedWords(this.tally)}></end-note>
      ${this.unlockedNow ? html`<div class="review-gate-unlocked small" role="status">${t('gate.unlocked')}</div>` : nothing}
      ${this.renderUnsaved()}
      ${status}
      <div class="tend-walk-actions">
        ${this.unlockedNow
          ? html`<button class="btn small review-gate-record" @click=${this.openCaptureAfterReview}>${t(this.hasUnheardDraft ? 'drafts.resumeRecording' : 'gate.newMoment')}</button>`
          : nothing}
        ${this.nextState === 'ready' && this.nextKey && this.glintsOnScreen === 0
          ? html`<button class="btn primary tend-next" ?disabled=${held} @click=${this.nextHouse}>${t('tend.nextHouse')}</button>`
          : nothing}
        <button class="btn ${this.nextKey && this.glintsOnScreen === 0 ? 'ghost' : 'primary'} tend-back" @click=${this.leave}>${t('tend.done')}</button>
      </div>
    </div>`;
  }

  /** The cards are over and bricks wait (or the answers are still landing): the bricks and the payoff's button. */
  private renderBeforeBuild() {
    const r = this.round;
    return html`<div class="round-after"><div class="card round-earned" style="text-align:center">
      ${this.renderStopped() || html`<div><strong>${t(this.lesson ? 'land.lessonComplete' : 'round.complete')}</strong></div>`}
      ${r.bricks.length
        ? html`${r.renderBricks()}
            <button class="btn primary block round-build" style="margin-top:12px" @click=${this.buildEarned}>${t('round.build')}</button>`
        : html`<div style="margin-top:10px"><span class="spinner"></span></div>`}
      ${this.renderUnsaved()}
      ${r.bricks.length ? html`<div class="end-actions">${this.lesson ? this.renderLessonEnd(false) : this.renderGateEnd(false)}</div>` : nothing}
    </div></div>`;
  }

  /** "Stopped here…", once the learner stopped before the last card. */
  private renderStopped() {
    if (!this.round.stopped) return '';
    return html`<div class="round-stopped"><strong>${t(this.lesson ? 'land.stopped' : 'round.stopped')}</strong></div>`;
  }

  /** The end's sheet under the town: its lines (`<end-note>`), then the way on. */
  private renderSheet() {
    const town = this.round.after;
    const conditionSummary = town?.buildings.some(b => b.condition)
      ? html`<section class="round-condition-summary" aria-labelledby="round-condition-title">
          <h2 id="round-condition-title">${t('town.conditionSummary')}</h2>
          <p>${t('town.maintenanceLights', { lamps: town.counts.lamps, ivy: town.counts.ivy })}</p>
          <p class="muted">${t('town.maintenanceExplain')}</p>
        </section>`
      : nothing;
    return html`<div class="end-sheet" @end-ready=${this.onEndReady}>
      <div class="end-sheet-body">
        ${this.renderStopped() ? html`<div class="small">${this.renderStopped()}</div>` : nothing}
        ${conditionSummary}
        <end-note ?round=${!this.tend && !this.lesson} .landed=${this.landed()} .summary=${this.summary()} .missed=${missedWords(this.tally)}></end-note>
        ${this.unlockedNow
          ? html`<div class="review-gate-unlocked small" role="status">${t('gate.unlocked')}</div>`
          : nothing}
        ${this.renderUnsaved()}
      </div>
      <div class="end-actions">
        ${this.unlockedNow
          ? html`<button class="btn primary block review-gate-record" @click=${this.openCaptureAfterReview}>${t(this.hasUnheardDraft ? 'drafts.resumeRecording' : 'gate.newMoment')}</button>`
          : nothing}
        ${this.tend
          ? html`<button class="btn primary block tend-back" @click=${this.leave}>${t('tend.backToTown')}</button>`
          : this.lesson ? this.renderLessonEnd(true) : this.renderGateEnd(!this.unlockedNow)}
      </div>
    </div>`;
  }

  /**
   * The houses still to tend, read after the saves settle (the round is at its end): the nearby ones to glint and the
   * pick for Next house. A failed read stays retriable and never claims the town is done; the end frames without
   * neighbours meanwhile.
   */
  private async findNext(): Promise<void> {
    if (this.tend?.kind !== 'word') return;
    const gen = ++this.nextGen, key = this.tend.key;
    this.nextState = 'loading';
    this.nextError = '';
    // What glints waits for the framing this read leads to.
    this.glintsOnScreen = null;
    try {
      const candidates = await api.townTending();
      if (!this.isConnected || gen !== this.nextGen) return;
      const cellOf = (k: string) => this.round.townEl?.stage?.cellOf(k) ?? null;
      this.nextKey = nextTend(candidates, key, this.tendedKeys, cellOf);
      this.nearby = nearbyTends(candidates, key, this.tendedKeys, cellOf);
      this.nextState = 'ready';
    } catch (e) {
      if (!this.isConnected || gen !== this.nextGen) return;
      this.nearby = [];
      this.nextState = 'error';
      this.nextError = errorText(e);
    }
    this.placeChipsSoon();
  }

  /** On to another house: home replaces this one's history entry and flies there (`tend-next`). */
  private tendNext(key: string) {
    if (this.unsaved.length || this.retrying || !this.isConnected) return;
    this.dispatchEvent(new CustomEvent('tend-next', { detail: key, bubbles: true, composed: true }));
  }

  private nextHouse = () => { if (this.nextKey) this.tendNext(this.nextKey); };

  private onGlint = (key: string) => this.tendNext(key);

  /** The end's counts: the score from the tally, fog lifted and houses climbed from the town before and after. */
  private summary(): EndSummary {
    const before = this.round.before ?? this.townBefore;
    return summaryOf(this.tally, { before, after: this.round.after, keys: this.allReachedKeys() });
  }

  private onEndReady = () => {
    if (!this.isConnected) return;
    this.endReady = true;
    this.placeChipsSoon();
  };

  /** The sheet is on screen: its size frames the chips (once its lines are there), and follows it as it changes. */
  private watchSheet() {
    const sheet = this.querySelector('.end-sheet, .tend-walk');
    if (!sheet) return;
    if (this.sheetSeen) return;
    this.sheetSeen = new ResizeObserver(this.placeChipsSoon);
    this.sheetSeen.observe(sheet);
    addEventListener('resize', this.placeChipsSoon);
    // The end's lines wait for the server; the town does not wait for them for long.
    this.endTimer = setTimeout(() => this.onEndReady(), 3000);
  }

  private placeChipsSoon = () => {
    cancelAnimationFrame(this.chipFrame);
    this.chipFrame = requestAnimationFrame(() => this.placeChips());
  };

  /**
   * The chips over the lots that changed, in the room the sheet leaves. A walk's end waits for the houses still to
   * tend as well, and frames the tended house with its nearby ones in one move; its bar growing a line never moves
   * the camera again (only the screen's size, or what is framed, does).
   */
  private placeChips() {
    if (!this.isConnected) return;
    const walk = this.walking();
    const sheet = this.querySelector(walk ? '.tend-walk' : '.end-sheet');
    const el = this.round.townEl;
    if (!this.endReady || !sheet || !el || this.round.phase !== 'built') return;
    if (walk && this.nextState !== 'ready' && this.nextState !== 'error') return;
    const chips = this.round.endChips(this.allReachedKeys(), this.tally.missed);
    const top = sheet.getBoundingClientRect().top;
    // A sheet being removed has no viewport room; it must never reframe the scene now owned by the next house.
    if (!sheet.isConnected || top <= 0) return;
    // end-ready, the observer's first call and a resize can land together: the camera flies once for the same view.
    const framed = chips.map((c) => `${c.key}:${c.kind}:${c.material ?? ''}`).join(',');
    const placed = walk
      ? `${innerWidth}x${innerHeight}|${framed}|${this.nearby.map((g) => g.key).join(',')}`
      : `${Math.round(top)}|${innerWidth}|${framed}`;
    if (placed === this.chipsPlaced) {
      // The bar changed height after the one flight, or a read of the houses left the framing as it was: the chips
      // and glints follow, the camera stays.
      const recount = this.glintsOnScreen === null && this.landedGen === this.placeGen;
      if (walk && (recount || (this.glintsOnScreen !== null && Math.round(top) !== this.walkTop))) {
        this.walkTop = Math.round(top);
        this.glintsOnScreen = el.placeChips(top);
      }
      return;
    }
    const ms = this.chipsPlaced ? 250 : 700;
    this.chipsPlaced = placed;
    if (!walk || this.tend?.kind !== 'word') { void el.showChips(chips, top, ms); return; }
    // Until this flight lands, what glints is unknown: Next house must not flash up for the glints it is placing.
    this.glintsOnScreen = null;
    const gen = ++this.placeGen;
    void el.showChips(chips, top, ms, { include: [this.tend.key], glints: this.nearby, onGlint: this.onGlint })
      .then((n) => {
        if (n === null || gen !== this.placeGen || !this.isConnected) return;
        this.landedGen = gen;
        // Placed for the bar as it stands now, in case it changed during the flight.
        const now = sheet.isConnected ? sheet.getBoundingClientRect().top : top;
        this.walkTop = Math.round(now);
        this.glintsOnScreen = now === top ? n : el.placeChips(now);
      });
  }

  /**
   * Home's renderer was replaced under the walk (WebGL context lost, the night town took over): the end's chips and
   * glints went with the old one, so the end frames again on the new one.
   */
  restage() {
    if (!this.done || !this.endReady) return;
    this.chipsPlaced = '';
    this.glintsOnScreen = null;
    this.placeChipsSoon();
  }

  /** The end is gone (another pass at the round, or the screen): its chips and watchers with it. */
  private clearEnd() {
    clearTimeout(this.endTimer);
    cancelAnimationFrame(this.chipFrame);
    this.sheetSeen?.disconnect();
    this.sheetSeen = null;
    removeEventListener('resize', this.placeChipsSoon);
    this.endReady = false;
    this.chipsPlaced = '';
    this.walkFocused = false;
    this.glintsOnScreen = null;
  }

  /** Cards the round reached: all of them once finished, else up to the current one if it was answered. */
  private reached(): number {
    if (this.done && !this.round.stopped) return this.queue.length;
    return Math.min(this.queue.length, this.i + (this.recordedTaskId === this.queue[this.i]?.task.id ? 1 : 0));
  }

  private reachedKeys(): string[] {
    return this.queue.slice(0, this.reached()).map((e) => e.item.key);
  }

  /** This segment's reached keys plus every earlier segment's this sitting (ticket 15 review): what "what changed" and Stop here must read, so a gate-split round never loses an earlier segment. */
  private allReachedKeys(): string[] {
    return [...new Set([...this.sittingReached, ...this.reachedKeys()])];
  }

  /** Every save on its way has landed or failed. */
  private landed(): Promise<unknown> {
    return Promise.allSettled([...this.inflight]);
  }

  /** Stop here: the round ends at this card; what is left of it waits for the next visit. */
  private stopHere = () => {
    if (this.done || this.tend) return;
    this.clearAutoAdvance();
    this.skipMenu = false;
    this.round.stop();
    this.done = true;
    void this.round.finish(this.landed());
  };

  private buildEarned = () => {
    void this.round.build(this.landed());
  };

  private renderGateEnd(homeFirst = true) {
    return html`
        ${this.gate?.status === 'pending'
          ? html`<div class="review-gate-more small">${t('gate.moreToGo', { remaining: this.gate.remaining })}</div>
              <button class="btn primary block review-gate-continue" @click=${this.continueReview}>${t('gate.continue')}</button>`
          : nothing}
        <button class="btn ${this.gate?.status === 'pending' || !homeFirst ? '' : 'primary'} block" @click=${() => go('/')}>${t('common.home')}</button>
    `;
  }

  /**
   * The first lesson's way out: its moment, then home. A newcomer whose question waits (the revamp): **Answer {name}**
   * first, then home, and the moment as a link under them.
   */
  private renderLessonEnd(momentFirst: boolean) {
    const id = this.lesson?.momentId;
    if (!id) return nothing;
    const answer = this.lesson?.answer;
    if (answer) {
      return html`
        <button class="btn primary block newcomers-answer" @click=${() => openAnswer(answer.momentId)}>${answerLabel(answer)}</button>
        <button class="btn block land-home" @click=${() => go('/')}>${t('common.home')}</button>
        <a class="land-open-moment lesson-end-link small" href="/moment/${id}" @click=${(e: Event) => { e.preventDefault(); go(`/moment/${id}`); }}>${t('tend.openMoment')}</a>`;
    }
    return html`
      <button class="btn ${momentFirst ? 'primary' : ''} block land-open-moment" @click=${() => go(`/moment/${id}`)}>${t('tend.openMoment')}</button>
      <button class="btn block land-home" @click=${() => go('/')}>${t('common.home')}</button>`;
  }

  private lessonTitle(x: TownLesson): string {
    return x.title ? t('land.lessonTitleOf', { title: x.title }) : t('land.lessonTitle');
  }

  private tendTitle(x: Tend): string {
    if (x.kind === 'word') return t('tend.titleWord');
    if (x.kind === 'road') return t('tend.titleRoad');
    const title = this.townBefore?.districts.find((d) => d.id === x.id)?.title;
    return title ? t('tend.titleDistrictOf', { title }) : t('tend.titleDistrict');
  }

  /** Catalan line to play aloud for listen tasks (hearing only — never shown as text). */
  private audioText(): string | null {
    return listenAudioText(this.queue[this.i]?.task.props as Record<string, unknown> | undefined);
  }

  /** The sign's ▶: the spot it line through the listen route (the device's voice when it can't), again to stop. */
  private playLine = () => {
    const e = this.queue[this.i];
    const words = e?.task.props.words as string[] | undefined;
    const text = words?.length ? words.join(' ') : this.audioText();
    if (e && text) void speak(e.momentId, text);
  };

  /**
   * A hanging spot it card reads its zone's parts from this view (`signParts`), so it draws again with every update
   * here; and the zone's height, wherever it is drawn, keeps the sign above it.
   */
  private updateSignZone() {
    const root = this.querySelector('a2ui-surface')?.shadowRoot;
    if (this.hanging()) root?.querySelectorAll<LitElement>('li-spotit, li-choice, li-gapfill, li-reorder, li-dictation, li-soundalike, li-sayfrommeaning, li-sayitback').forEach((c) => c.requestUpdate());
    const zone = this.querySelector<HTMLElement>(':scope > .sign-zone') ?? root?.querySelector<HTMLElement>('.sign-zone') ?? null;
    this.focusNext(zone);
    if (zone === this.zoneSeen) return;
    this.zoneSeen = zone;
    this.zoneWatch?.disconnect();
    this.zoneWatch = undefined;
    if (!zone) return this.style.removeProperty('--sign-zone-h');
    this.zoneWatch = new ResizeObserver(() => this.style.setProperty('--sign-zone-h', `${Math.ceil(zone.getBoundingClientRect().height)}px`));
    this.zoneWatch.observe(zone);
  }

  private nextFocusedAt = -1;

  /** A hanging card's Next appears once it is answered: focus moves to it once, so a keyboard or switch user lands on it. */
  private focusNext(zone: HTMLElement | null) {
    if (!zone || !this.answered || this.nextFocusedAt === this.i) return;
    const at = this.i;
    requestAnimationFrame(() => {
      const next = zone.querySelector<HTMLButtonElement>('.sign-foot .btn.primary:not(.show-me)');
      if (at !== this.i || !next || next.disabled) return;
      next.focus({ preventScroll: true });
      this.nextFocusedAt = at;
    });
  }

  private playAudio = () => {
    const e = this.queue[this.i];
    const a = this.audioText();
    // No mock/lang hints here (queue spans moments); speak() falls back to device speech if the server has no TTS.
    if (e && a) void speak(e.momentId, a);
  };

  private resetSurface() {
    this.processor?.processMessages([{ version: 'v0.9', deleteSurface: { surfaceId: SURFACE_ID } }]);
    this.processor?.processMessages([{ version: 'v0.9', createSurface: { surfaceId: SURFACE_ID, catalogId: CATALOG_ID } }]);
  }

  /** Flashcards grade in one tap — auto-advance so the learner never needs a second Next tap. */
  private scheduleAutoAdvance(delay = 650) {
    this.clearAutoAdvance();
    const i = this.i;
    this.autoTimer = setTimeout(() => {
      if (this.done || this.i !== i) return;
      this.advance();
    }, delay);
  }

  private clearAutoAdvance() {
    if (this.autoTimer !== undefined) clearTimeout(this.autoTimer);
    this.autoTimer = undefined;
  }

  private renderTask() {
    const e = this.queue[this.i];
    if (!e || !this.processor) return;
    this.resetSurface();
    try {
      this.processor.processMessages([{
        version: 'v0.9',
        updateComponents: {
          surfaceId: SURFACE_ID,
          components: [
            { id: 'root', component: 'Column', children: ['p', 'q'] },
            { id: 'p', component: 'Progress', done: this.i + 1, total: this.queue.length },
            { id: 'q', component: COMPONENT[e.task.type], ...e.task.props },
          ],
        },
      }] as never);
    } catch (err) {
      console.error('review render failed', err);
      this.error = t('lesson.cardError', { n: this.i + 1 });
    }
    void this.checkSpeakable();
    if (!this.tend || this.signs) this.round.enter(this.i, e);
  }

  /**
   * A say-it card (ticket 14) is asked by its reading alternative when no
   * check can be made: while tending (the town opens at will, and every check
   * is paid from the same allowance as new moments), after a check was refused
   * this session, or on a device with no microphone.
   */
  private async checkSpeakable() {
    const i = this.i;
    const e = this.queue[i];
    if (!e?.alternative || e.task.type !== 'sayfrommeaning') return;
    if (this.tend) return this.useAlternative('');
    if (this.checksOff) return this.useAlternative(t('review.sayItOff'));
    if (await hasMicrophone()) return;
    if (this.i === i && this.queue[i] === e && !this.done) this.useAlternative(t('gate.noMic'));
  }

  private useAlternative(note: string) {
    const e = this.queue[this.i];
    if (!e?.alternative) return;
    this.queue = this.queue.map((x, k) => (k === this.i ? withoutSaying(x) : x));
    this.micNote = note;
    this.resetPron();
    this.renderTask();
  }

  /** The pronunciation check is offered: always on a say it back, once per say-it card, never after "I said it". */
  private checkOpen(): boolean {
    return this.queue[this.i]?.task.type !== 'sayfrommeaning' || this.recordedTaskId !== this.queue[this.i]?.task.id;
  }

  /** The say-it card shows its word once it has been judged. */
  private revealWord() {
    const card = this.querySelector('a2ui-surface')?.shadowRoot?.querySelector('li-sayfrommeaning') as
      (Element & { reveal?: () => void }) | null | undefined;
    card?.reveal?.();
  }

  private onAction(action: A2uiClientAction) {
    if (this.done) return;
    const e = this.queue[this.i];
    // Hint reveals are rescue tiers: free, scoring-neutral, never unlock Next.
    if (action.name === 'hint') { this.exerciseEvent(e, 'hint'); return; }
    const ctx = (action.context ?? {}) as Record<string, unknown>;
    const hintsUsed = typeof ctx.hintsUsed === 'number' ? ctx.hintsUsed : undefined;
    // "My answer should count" is a log-only flag for tuning — no scheduling effect.
    if (action.name === 'dispute') {
      const given = typeof ctx.value === 'string' ? ctx.value.slice(0, 200) : undefined;
      void this.save(e, {
        taskId: e.task.id, itemKey: e.item.key, type: e.task.type, correct: false,
        skipped: true, skipReason: 'dispute', flagged: true, given, hintsUsed,
        production: e.task.production, productionKind: e.task.productionKind,
      });
      return;
    }
    // A flashcard flip only reveals — the verdict comes from the grade buttons.
    if (action.name === 'flipped') { this.exerciseEvent(e, 'reveal'); return; }
    if (this.recordedTaskId === e.task.id) {
      this.answered = true;
      if (this.showMe && !answerHidden(e.task.type, action.name, ctx)) this.answerSeen(e, ctx);
      else if (ctx.revealed === true) this.exerciseEvent(e, 'reveal');
      else if (action.name === 'answer' || action.name === 'grade') this.exerciseEvent(e, 'retry');
      return;
    }
    this.answered = true;
    this.recordedTaskId = e.task.id;
    // "I said it" is self-reported practice: saved unscored, never recall. A
    // say it back advances on its own; a say-it card stays to show its word.
    if (speakingCard(e.task.type) && action.name === 'ready') {
      void this.save(e, {
        taskId: e.task.id, itemKey: e.item.key, type: e.task.type,
        correct: true, production: true, productionKind: 'self_report', selfReport: true,
      });
      if (e.task.type === 'sayitback') this.scheduleAutoAdvance(400);
      else this.resetPron(); // a recording under way stops, and is never sent
      return;
    }
    const correct = action.name === 'answer' || action.name === 'grade' ? ctx.correct === true : true;
    // Ticket 32: never leave a miss without its answer on screen.
    this.showMe = answerHidden(e.task.type, action.name, ctx);
    if (this.showMe) this.skipMenu = false;
    // "I don't know" on a say-it card: no check is made, so a recording under way stops and is never sent.
    if (ctx.gaveUp === true) this.resetPron();
    void this.save(e, {
      taskId: e.task.id,
      itemKey: e.item.key,
      type: e.task.type,
      correct,
      revealed: ctx.revealed === true,
      production: e.task.production,
      productionKind: e.task.productionKind,
      // assessed is only for machine-scored pronunciation (togglePron below), never for typed/choice answers.
      hintsUsed,
      given: givenText(e.task.type, ctx, correct),
      // Contradiction flag: a miss on a forged word (box 3 and up) disputes
      // the verdict (never a spelling card's: its word already passed). The
      // first lesson follows practice, which sends none.
      flagged: !correct && e.item.forged && !e.spelling && !this.lesson ? true : undefined,
    });
    // A flashcard grade already is the verdict — no second Next tap needed.
    if (e.task.type === 'flashcard' && action.name === 'grade') this.scheduleAutoAdvance();
  }

  /**
   * The answer is on screen after a miss (ticket 32), so Next comes back. A
   * Show me is saved once, unscored, as a reveal; a right retry or a scratch
   * after the miss is not saved, as before.
   */
  private answerSeen(e: ReviewQueueEntry, ctx: Record<string, unknown>) {
    this.showMe = false;
    if (ctx.revealed !== true) return;
    this.exerciseEvent(e, 'reveal');
    void this.save(e, {
      taskId: e.task.id, itemKey: e.item.key, type: e.task.type, correct: false, revealed: true,
      production: e.task.production, productionKind: e.task.productionKind,
      hintsUsed: typeof ctx.hintsUsed === 'number' ? ctx.hintsUsed : undefined,
    });
  }

  /** The bar's Show me: the card shows its answer, and its reveal comes back through onAction. */
  private showAnswer = () => {
    if (!showAnswerOn(this)) this.showMe = false;
  };

  private resetPron() {
    this.pronStop.stop = true;
    this.pronPhase = 'idle';
    this.pronTip = '';
    this.pronError = '';
    this.pronLevel = 0;
  }

  /**
   * Tap to record, tap again to stop; the score counts as this card's
   * production attempt. A say-it card (ticket 14) takes one check: its
   * verdict is the answer, and the card then shows its word.
   */
  private togglePron = async () => {
    const i = this.i;
    const e = this.queue[i];
    if (!e || !speakingCard(e.task.type) || !this.checkOpen()) return;
    const sayIt = e.task.type === 'sayfrommeaning';
    /** A say-it card answered ("I said it") or left since the check began: nothing more is sent or saved for it. */
    const settled = () => sayIt && (this.recordedTaskId === e.task.id || this.i !== i || this.queue[i] !== e);
    if (this.pronPhase === 'recording') {
      this.pronStop.stop = true;
      return;
    }
    if (this.pronPhase !== 'idle') return;
    this.pronPhase = 'recording';
    this.pronTip = '';
    this.pronError = '';
    this.pronStop.stop = false;
    let recorded = false;
    try {
      const blob = await recordSnippet((l) => (this.pronLevel = l), this.pronStop);
      recorded = true;
      // "I said it" while recording: the card is answered, so nothing is checked.
      if (settled()) return;
      this.pronPhase = 'scoring';
      const r = await api.scorePronunciation(e.momentId, blob, String(e.task.props.ca ?? ''));
      this.pronPhase = 'idle';
      // Answered or left while the check ran: the verdict is not this card's answer any more.
      if (settled()) return;
      this.pronTip = r.pass ? t('lesson.pronPass') : t('lesson.pronFail', { tip: r.tip });
      if (this.recordedTaskId !== e.task.id) {
        this.recordedTaskId = e.task.id;
        await this.save(e, {
          taskId: e.task.id,
          itemKey: e.item.key,
          type: e.task.type,
          correct: r.pass,
          production: true,
          productionKind: 'pronunciation',
          assessed: true,
          // Contradiction flag: a miss on a forged word disputes the verdict (never in the first lesson, as in practice).
          flagged: !r.pass && e.item.forged && !this.lesson ? true : undefined,
        });
      }
      if (sayIt) {
        // One check: pass or miss, the word shows and Next waits for the learner.
        this.answered = true;
        if (this.queue[this.i] === e) this.revealWord();
      } else if (r.pass) {
        this.answered = true;
        this.scheduleAutoAdvance(900);
      }
    } catch (err) {
      this.pronPhase = 'idle';
      const here = this.queue[this.i] === e;
      // No check left (a spent allowance, or no scoring): this and every later
      // say-it card of the session ask the same word another way.
      const refused = sayIt && err instanceof HttpError && (err.status === 402 || err.status === 501);
      if (refused) this.checksOff = true;
      // A say-it card answered or left meanwhile keeps its answer, with no note.
      if (settled()) return;
      if (refused && e.alternative) {
        this.useAlternative(t('review.sayItOff'));
        return;
      }
      // No microphone: ask the same word another way instead.
      if (!recorded && e.alternative && here) {
        this.useAlternative(t('gate.noMic'));
        return;
      }
      this.pronError = errorText(err) || t('lesson.scoringFailed');
    }
  };

  private toggleSkipMenu = () => {
    this.skipMenu = !this.skipMenu;
  };

  private skipWithReason = (reason: SkipReason) => {
    if (this.done) return;
    const e = this.queue[this.i];
    this.clearAutoAdvance();
    this.skipMenu = false;
    if (this.recordedTaskId !== e.task.id) {
      this.recordedTaskId = e.task.id;
      void this.save(e, {
        taskId: e.task.id, itemKey: e.item.key, type: e.task.type,
        correct: false, skipped: true, skipReason: reason, production: e.task.production,
        productionKind: e.task.productionKind,
      });
    }
    this.advance();
  };

  /**
   * Remove a card from future review after explicit confirmation. Future packs are told
   * not to teach it as new (see learnerContext). Only advances once the
   * status update succeeds — on failure the card stays open with an error.
   */
  private removeCurrentCard = async () => {
    if (this.done) return;
    const e = this.queue[this.i];
    if (!e) return;
    if (!confirm(t('lesson.removeConfirm', { label: `${FSI}${(e.item.latin || e.item.ca).slice(0, 60)}${PDI}` }))) return;
    this.skipMenu = false;
    this.actionError = '';
    this.saving = true;
    const operationScope = `${e.momentId}:${e.task.id}`;
    const operationId = this.pendingRemovalOperations.get(operationScope) ?? makeClientId();
    this.pendingRemovalOperations.set(operationScope, operationId);
    try {
      const { previousStatus, statusVersion } = await api.setItemStatus(e.momentId, e.item.key, 'removed', { operationId });
      const countedDue = !!e.batch;
      const entries = this.queue.flatMap((entry, index) =>
        entry.item.key === e.item.key ? [{ index, entry }] : []);
      const earlierEntries = entries.filter(({ index }) => index < this.i).length;
      this.removedKeys = [...this.removedKeys, {
        momentId: e.momentId,
        itemKey: e.item.key,
        taskId: e.task.id,
        index: this.i,
        entries,
        countedDue,
        previousStatus: previousStatus ?? 'active',
        statusVersion: statusVersion ?? 0,
        answerSequence: this.answerSequence,
        answered: this.answered,
        showMe: this.showMe,
        recorded: this.recordedTaskId === e.task.id,
      }];
      // Remove every copy, including an easier card queued after a miss. The
      // saved entries let Undo restore the queue; account for earlier copies
      // when placing the cursor on the next card.
      this.queue = this.queue.filter((entry) => entry.item.key !== e.item.key);
      this.i -= earlierEntries + 1;
      this.pendingRemovalOperations.delete(operationScope);
      if (countedDue) {
        this.dueCount = Math.max(0, this.dueCount - 1);
        // A removed card leaves today's batch, so the requirement may have shrunk.
        void this.refreshGate();
      }
    } catch (err) {
      if (isItemStatusConflict(err)) this.pendingRemovalOperations.delete(operationScope);
      this.actionError = isItemStatusConflict(err)
        ? t('lesson.statusConflict')
        : t('lesson.removeFailed', { err: errorText(err) });
      return;
    } finally {
      this.saving = false;
    }
    this.advance();
  };

  private undoRemoval = async () => {
    let removed = this.removedKeys.at(-1);
    if (!removed || removed.answerSequence !== this.answerSequence) return;
    this.actionError = '';
    this.saving = true;
    if (!removed.undoOperationId) {
      removed = { ...removed, undoOperationId: makeClientId() };
      this.removedKeys = [...this.removedKeys.slice(0, -1), removed];
    }
    try {
      await api.setItemStatus(removed.momentId, removed.itemKey, removed.previousStatus, {
        expectedVersion: removed.statusVersion, operationId: removed.undoOperationId,
      });
      this.removedKeys = this.removedKeys.slice(0, -1);
      for (const saved of removed.entries) {
        const at = Math.min(saved.index, this.queue.length);
        this.queue = [...this.queue.slice(0, at), saved.entry, ...this.queue.slice(at)];
      }
      if (removed.countedDue) {
        this.dueCount += 1;
        void this.refreshGate();
      }
      this.i = Math.min(removed.index, this.queue.length - 1);
      this.done = false;
      this.answered = removed.answered;
      this.showMe = removed.showMe;
      this.skipMenu = false;
      this.recordedTaskId = removed.recorded ? removed.taskId : null;
      this.renderTask();
    } catch (err) {
      if (isItemStatusConflict(err)) {
        this.removedKeys = this.removedKeys.slice(0, -1);
        this.actionError = t('lesson.undoConflict');
      } else {
        this.actionError = t('lesson.undoFailed', { err: errorText(err) });
      }
    } finally { this.saving = false; }
  };

  private next = () => {
    if (!this.answered || this.showMe) return; // hard gate
    this.advance();
  };

  private advance() {
    this.clearAutoAdvance();
    stopSpeaking();
    if (this.i + 1 >= this.queue.length) {
      this.done = true;
      if (this.tend) void this.round.tendEnd(this.townBefore, this.landed());
      else void this.round.finish(this.landed());
      return;
    }
    this.i += 1;
    this.answered = false;
    this.showMe = false;
    this.skipMenu = false;
    this.actionError = '';
    this.micNote = '';
    this.resetPron();
    this.renderTask();
  }
}

/** A tended round's cards (v1 ticket 27): a district's run, a building's card, or a shared word across a road. */
function tendQueue(x: Tend): Promise<{ queue: ReviewQueueEntry[] }> {
  return x.kind === 'district' ? api.townRun(x.id)
    : x.kind === 'word' ? api.townBuildingCard(x.key)
    : api.townRoadCard(x.a, x.b, x.key);
}

/** The cards a pronunciation check can judge: say it back, and say it from the meaning (ticket 14). */
function speakingCard(type: string | undefined): boolean {
  return type === 'sayitback' || type === 'sayfrommeaning';
}

/** Is there any microphone to record with? Unknown counts as yes — let the learner try. */
async function hasMicrophone(): Promise<boolean> {
  const md = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined;
  if (!md || typeof md.getUserMedia !== 'function') return false;
  if (typeof md.enumerateDevices !== 'function') return true;
  try {
    return (await md.enumerateDevices()).some((d) => d.kind === 'audioinput');
  } catch {
    return true;
  }
}
